import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ReviewContext = createContext();

export const useReview = () => {
  const context = useContext(ReviewContext);
  if (!context) {
    throw new Error('useReview must be used within a ReviewProvider');
  }
  return context;
};

export const ReviewProvider = ({ children }) => {
  const [reviews, setReviews] = useState([]);
  const [globalStats, setGlobalStats] = useState({ averageRating: 0, total: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadReviewData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReviewData = useCallback(async (courseId = null) => {
    setLoading(true);
    try {
      const params = courseId ? { courseId, limit: 50 } : { limit: 50 };
      const { data } = await axios.get('/api/reviews', { params });
      if (data.success) {
        setReviews(data.reviews || []);
        setGlobalStats(data.stats || { averageRating: 0, total: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } });
      }
    } catch (err) {
      console.error('[ReviewContext] Erreur chargement avis:', err);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const getReviewsByCourse = (courseId) => {
    return reviews.filter(review => review.courseId === courseId);
  };

  // Les commentaires de réponse ne sont pas encore gérés côté API —
  // on retourne un tableau vide pour ne pas casser les composants existants.
  const getCommentsByReview = () => [];

  const addComment = () => null;

  const likeComment = () => null;

  const addReview = async (reviewData) => {
    try {
      const { data } = await axios.post('/api/reviews', {
        courseId: reviewData.courseId,
        rating:   reviewData.rating,
        title:    reviewData.title || '',
        content:  reviewData.content,
      });
      if (data.success) {
        setReviews(prev => [data.review, ...prev]);
        return data.review;
      }
      throw new Error(data.error || 'Erreur lors de la soumission');
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      throw new Error(msg);
    }
  };

  const markReviewHelpful = (reviewId) => {
    setReviews(prev => prev.map(review =>
      review.id === reviewId
        ? { ...review, helpful: review.helpful + 1 }
        : review
    ));
  };

  const getAverageRating = (courseId) => {
    const courseReviews = getReviewsByCourse(courseId);
    if (courseReviews.length === 0) {
      // Si on filtre par cours, utiliser les stats globales
      return courseId ? 0 : globalStats.averageRating;
    }
    const total = courseReviews.reduce((sum, r) => sum + r.rating, 0);
    return parseFloat((total / courseReviews.length).toFixed(1));
  };

  const getRatingDistribution = (courseId) => {
    const courseReviews = getReviewsByCourse(courseId);
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    courseReviews.forEach(review => {
      if (review.rating >= 1 && review.rating <= 5) {
        distribution[review.rating]++;
      }
    });
    return distribution;
  };

  const value = {
    reviews,
    globalStats,
    loading,
    loadReviewData,
    getReviewsByCourse,
    getCommentsByReview,
    addReview,
    addComment,
    markReviewHelpful,
    likeComment,
    getAverageRating,
    getRatingDistribution,
  };

  return (
    <ReviewContext.Provider value={value}>
      {children}
    </ReviewContext.Provider>
  );
};
